// src/pages/AdminBannersPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import Loader from "../layouts/components/Loader";
import toast from "react-hot-toast";
import "./AdminBannersPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminBannersPage() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBanner, setDeletingBanner] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    image: null,
    video: null,
    media_type: "image", // "image" or "video"
    sort_order: "",
    is_active: true,
  });

  // ===== Helpers =====
  const getToken = () =>
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";

  const resolveMediaUrl = (item) => {
    if (!item) return null;
    if (typeof item.image_url === "string" && item.image_url) return item.image_url;
    if (typeof item.image_path === "string" && item.image_path) return item.image_path;
    if (typeof item.image === "string" && item.image) return item.image;
    if (item.image && typeof item.image === "object") {
      if (item.image.url) return item.image.url;
      if (item.image.path)
        return `${API_BASE.replace("/api", "")}/storage/${item.image.path}`;
    }
    return null;
  };

  const getMediaType = (item) => {
    if (!item) return "image";
    if (item.media_type) return item.media_type;
    if (item.is_video) return "video";
    return "image";
  };

  useEffect(() => {
    const token = getToken();
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    fetchBanners(token);
  }, [navigate]);

  const fetchBanners = async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/banners/admin`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Accept several shapes:
        // { data: { data: [...] } } (Laravel paginator nested)
        // { data: [...] }
        // [] bare array
        const list = Array.isArray(data?.data?.data)
          ? data.data.data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        setBanners(list);
      } else {
        toast.error("Failed to fetch banners");
        setBanners([]);
      }
    } catch (error) {
      toast.error("Error fetching banners");
      console.error("Error:", error);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]:
          type === "checkbox" ? checked :
          type === "file" ? files[0] :
          value,
      };
      
      // Clear the opposite media type when switching
      if (name === "media_type") {
        if (value === "image") {
          newData.video = null;
        } else if (value === "video") {
          newData.image = null;
        }
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    setIsSubmitting(true);

    // 1) Client-side guards
    const isEdit = Boolean(editingBanner);

    // image/video required only on create
    if (!isEdit && !formData.image && !formData.video) {
      toast.error("Banner image or video is required");
      return;
    }

    // normalize sort_order -> number (default 0)
    const sortOrderNum =
      formData.sort_order === "" || formData.sort_order === null || formData.sort_order === undefined
        ? 0
        : Number(formData.sort_order);

    if (Number.isNaN(sortOrderNum) || sortOrderNum < 0) {
      toast.error("Sort order must be a number ≥ 0");
      return;
    }

    // 2) Build FormData
    const fd = new FormData();
    // Send the appropriate file based on media type
    if (formData.media_type === "video" && formData.video) {
      fd.append("image", formData.video); // Backend expects 'image' field for both images and videos
    } else if (formData.media_type === "image" && formData.image) {
      fd.append("image", formData.image);
    }
    fd.append("sort_order", String(sortOrderNum));
    fd.append("is_active", formData.is_active ? "1" : "0");

    const url = isEdit
      ? `${API_BASE}/banners/${editingBanner.id}`
      : `${API_BASE}/banners`;

    // Spoof PUT on edit (safer with multipart on many PHP hosts)
    if (isEdit) fd.append("_method", "PUT");

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: fd,
      });

      if (response.ok) {
        toast.success(isEdit ? "Banner updated successfully" : "Banner created successfully");
        setShowModal(false);
        setEditingBanner(null);
        resetForm();
        fetchBanners(token);
      } else {
        const errorData = await response.json().catch(() => ({}));
        // Show first validation error if present
        if (errorData?.errors && typeof errorData.errors === "object") {
          const firstKey = Object.keys(errorData.errors)[0];
          const firstMsg = errorData.errors[firstKey]?.[0];
          toast.error(firstMsg || errorData.message || "Failed to save banner");
        } else {
          toast.error(errorData.message || "Failed to save banner");
        }
        console.error("Save error detail:", errorData);
      }
    } catch (error) {
      toast.error("Error saving banner");
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      image: null, // don't preload file
      video: null, // don't preload file
      media_type: getMediaType(banner),
      sort_order:
        banner?.sort_order === null || banner?.sort_order === undefined
          ? ""
          : String(banner.sort_order),
      is_active: Boolean(banner?.is_active),
    });
    setShowModal(true);
  };

  const handleDelete = async (bannerId) => {
    const banner = banners.find(b => b.id === bannerId);
    setDeletingBanner(banner);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingBanner) return;
    
    setIsDeleting(true);
    const token = getToken();

    try {
      const response = await fetch(`${API_BASE}/banners/${deletingBanner.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        toast.success("Banner deleted successfully");
        setShowDeleteModal(false);
        setDeletingBanner(null);
        fetchBanners(token);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.message || "Failed to delete banner");
      }
    } catch (error) {
      toast.error("Error deleting banner");
      console.error("Error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingBanner(null);
    setIsDeleting(false);
  };

  const resetForm = () => {
    setFormData({
      image: null,
      video: null,
      media_type: "image",
      sort_order: "",
      is_active: true,
    });
  };

  const openModal = () => {
    setEditingBanner(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBanner(null);
    resetForm();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-banners">
          <div className="loading">Loading banners...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-banners">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Banners Management</h1>
            <p>Manage homepage banners and promotional content</p>
          </div>
          <div className="admin-actions">
            <button className="btn-primary" onClick={openModal}>
              <svg viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" />
              </svg>
              Add New Banner
            </button>
          </div>
        </div>

        {/* Banners Grid */}
        <div className="banners-grid">
          {banners.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24">
                  <path
                    d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2l1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <h3>No banners found</h3>
              <p>Create your first banner to get started</p>
            </div>
          ) : (
            banners.map((banner) => {
              const mediaUrl = resolveMediaUrl(banner);
              const mediaType = getMediaType(banner);
              return (
                <div key={banner.id} className="banner-card">
                  <div className="banner-image">
                    {mediaUrl ? (
                      mediaType === "video" ? (
                        <video
                          src={mediaUrl}
                          muted
                          loop
                          playsInline
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            if (e.currentTarget.nextSibling) {
                              e.currentTarget.nextSibling.style.display = "flex";
                            }
                          }}
                        />
                      ) : (
                        <img
                          src={mediaUrl}
                          alt={`Banner ${banner.id}`}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            if (e.currentTarget.nextSibling) {
                              e.currentTarget.nextSibling.style.display = "flex";
                            }
                          }}
                        />
                      )
                    ) : null}
                    <div
                      className="image-placeholder"
                      style={{ display: mediaUrl ? "none" : "flex" }}
                    >
                      <svg viewBox="0 0 24 24">
                        <path
                          d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2l1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="banner-info">
                    <div className="banner-details">
                      <div className="banner-id">
                        <span className="label">ID:</span>
                        <span className="value">#{banner.id}</span>
                      </div>
                      <div className="banner-sort">
                        <span className="label">Sort Order:</span>
                        <span className="value">
                          {banner.sort_order ?? 0}
                        </span>
                      </div>
                      <div className="banner-status">
                        <span className="label">Status:</span>
                        <span className={`status ${banner.is_active ? "active" : "inactive"}`}>
                          {banner.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="banner-media-type">
                        <span className="label">Type:</span>
                        <span className={`media-type ${mediaType}`}>
                          {mediaType === "video" ? "Video" : "Image"}
                        </span>
                      </div>
                      <div className="banner-date">
                        <span className="label">Created:</span>
                        <span className="value">
                          {banner.created_at
                            ? new Date(banner.created_at).toLocaleDateString()
                            : "-"}
                        </span>
                      </div>
                    </div>

                    <div className="banner-actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(banner)}
                        title="Edit Banner"
                      >
                        <svg viewBox="0 0 24 24">
                          <path
                            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                          <path
                            d="M18.5 2.5a2 2 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                        </svg>
                        Edit
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(banner.id)}
                        title="Delete Banner"
                      >
                        <svg viewBox="0 0 24 24">
                          <path
                            d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                          <line x1="10" y1="11" x2="10" y2="17" stroke="currentColor" strokeWidth="2" />
                          <line x1="14" y1="11" x2="14" y2="17" stroke="currentColor" strokeWidth="2" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingBanner ? "Edit Banner" : "Add New Banner"}</h2>
                <button className="modal-close" onClick={closeModal}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label htmlFor="media_type">Media Type</label>
                  <select
                    id="media_type"
                    name="media_type"
                    value={formData.media_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="image">Image</option>
                    <option value="video">Video (MP4)</option>
                  </select>
                </div>

                {formData.media_type === "image" ? (
                  <div className="form-group">
                    <label htmlFor="image">Banner Image</label>
                    <input
                      type="file"
                      id="image"
                      name="image"
                      onChange={handleInputChange}
                      accept="image/*"
                      required={!editingBanner}
                    />
                    {editingBanner && getMediaType(editingBanner) === "image" && resolveMediaUrl(editingBanner) && (
                      <div className="current-image">
                        <p>Current image:</p>
                        <img src={resolveMediaUrl(editingBanner)} alt="Current" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="form-group">
                    <label htmlFor="video">Banner Video</label>
                    <input
                      type="file"
                      id="video"
                      name="video"
                      onChange={handleInputChange}
                      accept="video/mp4"
                      required={!editingBanner}
                    />
                    {editingBanner && getMediaType(editingBanner) === "video" && resolveMediaUrl(editingBanner) && (
                      <div className="current-video">
                        <p>Current video:</p>
                        <video src={resolveMediaUrl(editingBanner)} controls muted />
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="sort_order">Sort Order</label>
                  <input
                    type="number"
                    id="sort_order"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="Enter sort order (lower numbers appear first)"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    <span>Active Banner</span>
                  </label>
                  <small className="form-help">
                    Only active banners will be displayed on the website
                  </small>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader size={16} strokeWidth={2} />
                        {editingBanner ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      editingBanner ? "Update Banner" : "Create Banner"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title="Delete Banner"
          message="Are you sure you want to delete this banner?"
          itemName={deletingBanner ? `Banner #${deletingBanner.id}` : ""}
          isLoading={isDeleting}
        />
      </div>
    </AdminLayout>
  );
}
